import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import axios from 'axios';
import { Calendar, MapPin, Users, Banknote, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter(event => {
    if (activeTab === 'all') return true;
    return event.type === activeTab;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-20 w-64 h-64 bg-[#E8A5C4]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#C45B9F]/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div
            className={`text-center max-w-3xl mx-auto transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="text-[#C45B9F] font-medium uppercase tracking-wider text-sm">
              Nos rendez-vous
            </span>
            <h1 className="font-heading text-5xl lg:text-6xl text-[#3D2E35] mt-4">
              Événements & <span className="font-script text-[#8B4B7C]">Workshops</span>
            </h1>
            <p className="text-lg text-[#6B5A62] mt-6">
              Participez à nos ateliers floraux et découvrez l'art des créations tropicales
            </p>
          </div>
        </div>
      </section>

      {/* Tabs & Events */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tabs */}
          <div
            className={`flex justify-center mb-12 transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="inline-flex bg-white rounded-full p-1 shadow-sm">
              {[
                { value: 'all', label: 'Tous' },
                { value: 'workshop', label: 'Workshops' },
                { value: 'evenement', label: 'Événements' }
              ].map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    activeTab === tab.value
                      ? 'bg-[#8B4B7C] text-white'
                      : 'text-[#6B5A62] hover:text-[#8B4B7C]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Events Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#8B4B7C] border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl">
              <Calendar className="mx-auto text-[#E8A5C4]" size={48} />
              <p className="text-[#6B5A62] mt-4 text-lg">Aucun événement prévu pour le moment</p>
              <p className="text-[#6B5A62] mt-2">Revenez bientôt pour découvrir nos prochains ateliers !</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredEvents.map((event, index) => {
                const spotsLeft = event.max_participants - event.current_participants;
                const isFull = spotsLeft <= 0;
                
                return (
                  <div
                    key={event.id}
                    className={`card-tevaite overflow-hidden transition-all duration-700 ${
                      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                    style={{ transitionDelay: `${(index + 2) * 100}ms` }}
                  >
                    {/* Image */}
                    <div className="aspect-video relative bg-gradient-to-br from-[#E8A5C4]/20 to-[#C45B9F]/20">
                      {event.image_url ? (
                        <img
                          src={event.image_url}
                          alt={event.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Calendar className="text-[#C45B9F]/40" size={48} />
                        </div>
                      )}
                      <div className="absolute top-4 left-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          event.type === 'workshop'
                            ? 'bg-[#5A8F6E] text-white'
                            : 'bg-[#8B4B7C] text-white'
                        }`}>
                          {event.type === 'workshop' ? 'Workshop' : 'Événement'}
                        </span>
                      </div>
                      {isFull && (
                        <div className="absolute top-4 right-4">
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-500 text-white">
                            Complet
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <h3 className="font-heading text-xl text-[#3D2E35]">{event.title}</h3>
                      <p className="text-[#6B5A62] mt-2 line-clamp-2">{event.description}</p>
                      
                      {/* Details */}
                      <div className="mt-4 space-y-2 text-sm text-[#6B5A62]">
                        <div className="flex items-center">
                          <Calendar size={16} className="mr-2 text-[#C45B9F]" />
                          <span>{formatDate(event.date)} à {formatTime(event.date)}</span>
                        </div>
                        <div className="flex items-center">
                          <MapPin size={16} className="mr-2 text-[#C45B9F]" />
                          <span>{event.location}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Users size={16} className="mr-2 text-[#C45B9F]" />
                            <span>{spotsLeft > 0 ? `${spotsLeft} places restantes` : 'Complet'}</span>
                          </div>
                          <div className="flex items-center font-medium text-[#8B4B7C]">
                            <Banknote size={16} className="mr-1" />
                            {event.price.toLocaleString()} XPF
                          </div>
                        </div>
                      </div>

                      {/* CTA */}
                      <div className="mt-6">
                        <Link
                          to={`/events/${event.id}`}
                          className={`w-full inline-flex items-center justify-center px-6 py-3 rounded-xl font-medium transition-all ${
                            isFull
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : 'btn-tevaite text-white'
                          }`}
                        >
                          {isFull ? 'Complet' : 'Réserver'}
                          {!isFull && <ArrowRight className="ml-2" size={18} />}
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EventsPage;
